import React, { useEffect, useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { RefreshCw, Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const UserSpendTable = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('amount_gross');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [start, setStart] = useState(() => new Date(Date.now() - 30*24*60*60*1000).toISOString());
  const [end, setEnd] = useState(() => new Date().toISOString());

  const fetchPage = async (p = page, sortByOverride = null, sortDirOverride = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = new URL(`${API_BASE_URL}/api/admin/reports/user-spend/paged`);
      url.searchParams.set('page', String(p));
      url.searchParams.set('page_size', String(pageSize));
      url.searchParams.set('sort_by', sortByOverride ?? sortBy);
      url.searchParams.set('sort_dir', sortDirOverride ?? sortDir);
      url.searchParams.set('start_date', start);
      url.searchParams.set('end_date', end);
      if (search.trim()) url.searchParams.set('q', search.trim());
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to fetch user spend');
      const data = await resp.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || p);
      setPageSize(data.page_size || pageSize);
      setSortBy(data.sort_by || sortBy);
      setSortDir(data.sort_dir || sortDir);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchPage(1); }, []);

  const pages = Math.ceil(total / pageSize) || 1;

  const exportXlsx = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = new URL(`${API_BASE_URL}/api/admin/reports/user-spend/export`);
      url.searchParams.set('start_date', start);
      url.searchParams.set('end_date', end);
      if (search.trim()) url.searchParams.set('q', search.trim());
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const a = document.createElement('a');
      const urlBlob = URL.createObjectURL(blob);
      a.href = urlBlob; a.download = 'user_spend.xlsx'; a.click();
      URL.revokeObjectURL(urlBlob);
    } catch (e) { console.error(e); alert(e.message || 'Export failed'); }
  };

  const header = (label, key) => (
    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
        onClick={() => { const nd = (sortBy === key && sortDir === 'desc') ? 'asc' : 'desc'; setSortBy(key); setSortDir(nd); fetchPage(1, key, nd); }}>
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </th>
  );

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (s) => {
    // Backend now returns pre-formatted strings (dd/mm/yyyy HH:MM:SS), just validate and return
    if (!s) return '';
    if (typeof s === 'string' && s.includes('/')) return s;  // Already formatted by backend
    // Fallback: try to parse and format to dd/mm/yyyy HH:MM:SS
    try {
      const d = new Date(s);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return s;
    }
  };

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Card.Title>User Spend</Card.Title>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <input placeholder="Search user email" value={search} onChange={e => setSearch(e.target.value)}
                     onKeyDown={e => { if (e.key === 'Enter') fetchPage(1); }}
                     style={{ padding: '8px 28px 8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, width: 220 }} />
              <Search size={14} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            </div>
            <select value={sortBy} onChange={e => { const newSort = e.target.value; setSortBy(newSort); fetchPage(1, newSort, sortDir); }} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <option value="amount_gross">Amount Gross</option>
              <option value="payments_count">Payments Count</option>
              <option value="last_payment">Last Payment</option>
            </select>
            <input type="date" value={start.split('T')[0]} onChange={e => setStart(new Date(e.target.value).toISOString())} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }} />
            <input type="date" value={end.split('T')[0]} onChange={e => setEnd(new Date(e.target.value).toISOString())} style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }} />
            <Button variant="secondary" onClick={() => fetchPage(1)} icon={<RefreshCw size={14} />}>Refresh</Button>
            <Button variant="primary" onClick={exportXlsx} icon={<Download size={14} />}>Export XLSX</Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>User ID</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Full Name</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Company</th>
                {header('Payments', 'payments_count')}
                {header('Amount Gross', 'amount_gross')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Refunded</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Net Collected</th>
                {header('Last Payment', 'last_payment')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>First Payment</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>Distinct Plans</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="12" style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="12" style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No records</td></tr>
              ) : (
                items.map((r, idx) => (
                  <tr key={`${r.user_id}_${idx}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>{r.user_id}</td>
                    <td style={{ padding: '10px 12px' }}>{r.user_email}</td>
                    <td style={{ padding: '10px 12px' }}>{r.user_full_name}</td>
                    <td style={{ padding: '10px 12px' }}>{r.company}</td>
                    <td style={{ padding: '10px 12px' }}>{r.payments_count}</td>
                    <td style={{ padding: '10px 12px' }}>{fmt(r.amount_gross)}</td>
                    <td style={{ padding: '10px 12px' }}>{fmt(r.refunded_amount)}</td>
                    <td style={{ padding: '10px 12px' }}>{fmt(r.net_collected)}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtDate(r.last_payment)}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtDate(r.first_payment)}</td>
                    <td style={{ padding: '10px 12px' }}>{r.distinct_plans_count}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Page {page} of {Math.ceil(total / pageSize) || 1} • {total} total</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => { if (page > 1) { const np = page - 1; setPage(np); fetchPage(np); } }} disabled={page === 1} icon={<ChevronLeft size={14} />}>Prev</Button>
            <Button variant="secondary" onClick={() => { const pages = Math.ceil(total / pageSize) || 1; if (page < pages) { const np = page + 1; setPage(np); fetchPage(np); } }} disabled={page >= (Math.ceil(total / pageSize) || 1)} icon={<ChevronRight size={14} />}>Next</Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default UserSpendTable;